import { AuthData } from "../auth";
import {
  RestUserFeed,
  RestReadMarkerPatch,
  FeedReadMarkerPatch,
  PostMarker
} from "../../schema-copy/api-schema";
import { patchReadMarkers } from "../api";
import { mergeFeedPatches, decodeBitmaps, parsePausedUntil, nowSemi } from "../../schema-copy/readmarker-helper";
import { getSinglePost } from "../post-engine";
import { recordMarkReadNow } from "../lastMarkRead";

export type UnreadChangeCallback = (feedId: string, unreadCount: number) => void;

// Local feed marker for easier manipulation
// Unified representation using bitmap + newestRead instead of 4 different modes
export interface LocalFeedMarker {
  feedId: string;
  sortOrder: 'N' | 'O' | 'A' | '?';
  showRead: 'u' | 'a';
  compactMode: 'c' | 'f' | 'l';  // 'c' for compact, 'f' for full, 'l' for list
  maxUnread?: number;   // auto-read limit: keep at most this many newest posts unread
  maxAgeDays?: number;  // auto-read limit: posts older than this are marked read
  pausedUntil?: number; // semi the pause ends (PAUSED_INDEFINITELY = never); may lie in the past
  bitmap: Uint8Array;
  unreadCount: number;
  newestRead?: number;  // index of newest (highest index) read post
}

export interface SerializedFeedMarker {
  feedId: string;
  sortOrder: 'N' | 'O' | 'A' | '?';
  showRead: 'u' | 'a';
  compactMode: 'c' | 'f' | 'l';
  maxUnread?: number;
  maxAgeDays?: number;
  pausedUntil?: number;
  bitmap: string; // base64 encoded
  unreadCount: number;
  newestRead?: number;
  /** Sender's postCount when serializing. The
   *  bitmap's bit 0 is the sender's minTrackable (basis - MAX_TRACKED_POSTS), so a
   *  receiver whose basis differs must remap the bits into its own frame — without
   *  this, tabs that disagree on postCount (one hasn't polled yet) shift every bit
   *  and silently corrupt each other's read state. Optional for messages from
   *  older tabs, which are applied unmapped as before. */
  basisPostCount?: number;
}

/** Per-feed auto-mark-read limits; 0/undefined = off */
export interface FeedLimits {
  maxUnread?: number;
  maxAgeDays?: number;
}

export const MAX_TRACKED_POSTS = 1000;
const SYNC_DELAY_MS = 3000;
const MAX_PENDING_EVENTS = 100;
// Watermark sentinel for a patch that has been POSTed but whose server response
// hasn't arrived yet. Larger than any real serverTime, so it's never treated as
// confirmed (and therefore always re-layered) until the real value lands.
const IN_FLIGHT = Number.POSITIVE_INFINITY;

/**
 * A read-marker patch that has been sent to (or is in flight to) the server but
 * is not yet provably reflected in server-pushed marker state. We keep re-laying
 * it on top of any server overwrite (poll / full load / cross-tab broadcast)
 * until a poll/load arrives whose serverTime >= this patch's serverTime — at
 * which point the server response is guaranteed to already contain it.
 */
interface UnconfirmedPatch {
  /** serverTime watermark from the POST response, or IN_FLIGHT until it resolves. */
  serverTime: number;
  patch: RestReadMarkerPatch;
  /** Per-feed postCount captured when the
   *  change was made, so markAllRead replay doesn't mark posts that arrived
   *  afterwards as read. */
  postCounts: Record<string, number>;
}

// Debug helper: render a list of post markers (single indices or [start,end] ranges)
// as a compact, coalesced range string like "3, 5-9, 42" for read-marker logging.
function formatPostMarkers(markers: PostMarker[] | undefined): string {
  if (!markers?.length) return '∅';
  const ranges = markers.map<[number, number]>(m => typeof m == 'number' ? [m, m] : [m[0], m[1]]);
  ranges.sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [];
  for (const [lo, hi] of ranges) {
    const last = merged[merged.length - 1];
    if (last && lo <= last[1] + 1) last[1] = Math.max(last[1], hi);
    else merged.push([lo, hi]);
  }
  return merged.map(([lo, hi]) => lo == hi ? `${lo}` : `${lo}-${hi}`).join(', ');
}

export interface MarkerEngineDeps {
  getAuth: () => AuthData | null;
  getFeed: (feedId: string) => RestUserFeed | undefined;
  getFeeds: () => RestUserFeed[];
  // Called after local mutation OR server-driven update of one or more markers
  // so the host can broadcast the new state cross-tab. Implementation is free
  // to no-op if there is no channel.
  broadcastMarkers: (feedIds: string[], serialized: SerializedFeedMarker[]) => void;
  // Called whenever the unsent (pending) patch changes: a change was queued, a send failed and its
  // changes went back into pending, or a send succeeded. Lets the host persist unsent changes.
  onPendingChange?: (event: 'queued' | 'failed' | 'sent') => void;
}

/** The consolidated not-yet-sent marker changes, with the post count each feed's change was made at. */
export interface PendingMarkers {
  patch: RestReadMarkerPatch;
  postCounts: Record<string, number>;
}

export class ReadMarkerEngine {
  private parsedMarkers: Map<string, LocalFeedMarker> = new Map();

  // Read marker sync state
  private pendingPatch: RestReadMarkerPatch = {};
  // Per-feed postCount at the time each
  // pending change was queued; mirrors UnconfirmedPatch.postCounts.
  private pendingPostCounts: Record<string, number> = {};
  private pendingEventCount = 0;
  private syncTimer: ReturnType<typeof setTimeout> | null = null;
  // POSTed-but-not-yet-confirmed patches, oldest first. See UnconfirmedPatch.
  private unconfirmed: UnconfirmedPatch[] = [];

  private unreadCallbacks: Set<UnreadChangeCallback> = new Set();
  // While true, marker mutations don't emit unread notifications — used during
  // replay so the caller can emit a single net-change notification per feed.
  private suppressNotify = false;

  constructor(private deps: MarkerEngineDeps) {}

  // --- Callbacks ---

  onUnreadChange(callback: UnreadChangeCallback): () => void {
    this.unreadCallbacks.add(callback);
    return () => this.unreadCallbacks.delete(callback);
  }

  private notifyUnreadChange(feedId: string) {
    if (this.suppressNotify) return;
    const unreadCount = this.getUnreadCount(feedId);
    this.unreadCallbacks.forEach(cb => {
      try { cb(feedId, unreadCount); } catch (e) { console.error('Unread callback error:', e); }
    });
  }

  // --- Read API ---

  getSortOrder(feedId: string): 'N' | 'O' | 'A' | '?' {
    return this.parsedMarkers.get(feedId)?.sortOrder ?? 'A';
  }

  setSortOrder(feedId: string, order: 'N' | 'O' | 'A'): void {
    const parsed = this.parsedMarkers.get(feedId);
    if (parsed) parsed.sortOrder = order;
    this.queueMarkerChange(feedId, { order });
    this.broadcast([feedId]);
  }

  getShowRead(feedId: string): 'u' | 'a' {
    return this.parsedMarkers.get(feedId)?.showRead ?? 'u';
  }

  setShowRead(feedId: string, showRead: 'u' | 'a'): void {
    const parsed = this.parsedMarkers.get(feedId);
    if (parsed) parsed.showRead = showRead;
    this.queueMarkerChange(feedId, { showRead });
    this.broadcast([feedId]);
    // The sidebar/home switch between unread count and a plain dot based on showRead,
    // so nudge unread-change listeners to re-render even though the count is unchanged.
    this.notifyUnreadChange(feedId);
  }

  getCompactMode(feedId: string): 'c' | 'f' | 'l' {
    return this.parsedMarkers.get(feedId)?.compactMode ?? 'f';
  }

  setCompactMode(feedId: string, mode: 'c' | 'f' | 'l'): void {
    const parsed = this.parsedMarkers.get(feedId);
    if (parsed) parsed.compactMode = mode;
    this.queueMarkerChange(feedId, { compactMode: mode });
    this.broadcast([feedId]);
  }

  getFeedLimits(feedId: string): FeedLimits {
    const parsed = this.parsedMarkers.get(feedId);
    return { maxUnread: parsed?.maxUnread, maxAgeDays: parsed?.maxAgeDays };
  }

  /** Sets the auto-mark-read limits for a feed; 0 = off. */
  setFeedLimits(feedId: string, maxUnread: number, maxAgeDays: number): void {
    const parsed = this.parsedMarkers.get(feedId);
    if (parsed) {
      parsed.maxUnread = maxUnread || undefined;
      parsed.maxAgeDays = maxAgeDays || undefined;
    }
    this.queueMarkerChange(feedId, { maxUnread, maxAgeDays });
    this.broadcast([feedId]);
    // The header's limits badge and the FeedTile's settings summary show the limits, so
    // nudge unread-change listeners to re-render (the auto-read pass follows later).
    this.notifyUnreadChange(feedId);
  }

  /** Semi time the feed's pause ends (PAUSED_INDEFINITELY = no end), or undefined when not paused.
   *  It may lie in the past: an ended pause stays until the engine marked the posts it covered. */
  getPausedUntil(feedId: string): number | undefined {
    return this.parsedMarkers.get(feedId)?.pausedUntil;
  }

  /** True while the feed is paused (its pause hasn't ended yet). */
  isPaused(feedId: string, nowMs = Date.now()): boolean {
    const until = this.getPausedUntil(feedId);
    return !!until && until > nowSemi(nowMs);
  }

  /** Sets the pause end (semi, PAUSED_INDEFINITELY = no end); 0 unpauses. Marks nothing itself. */
  setPausedUntil(feedId: string, pausedUntil: number): void {
    const parsed = this.parsedMarkers.get(feedId);
    if (parsed) parsed.pausedUntil = pausedUntil || undefined;
    this.queueMarkerChange(feedId, { pausedUntil });
    this.broadcast([feedId]);
    // The sidebar and home show paused feeds differently even when the count is unchanged.
    this.notifyUnreadChange(feedId);
  }

  /** Counts unread posts with index <= boundary (within the trackable window). In-memory only. */
  countUnreadAtOrBelow(feedId: string, boundary: number): number {
    const parsed = this.parsedMarkers.get(feedId);
    if (!parsed) return 0;
    const postCount = this.markerPostCount(this.deps.getFeed(feedId));
    const minTrackable = this.getMinTrackable(postCount);
    let count = 0;
    for (let i = minTrackable; i <= boundary && i < postCount; i++)
      if (!this.isPostReadLocalDirect(parsed.bitmap, parsed.newestRead, minTrackable, i))
        count++;
    return count;
  }

  getFeedMarker(feedId: string): LocalFeedMarker | undefined {
    return this.parsedMarkers.get(feedId);
  }

  getAllFeedMarkers(): Map<string, LocalFeedMarker> {
    return new Map(this.parsedMarkers);
  }

  isPostRead(feedId: string, postIndex: number): boolean {
    const parsed = this.parsedMarkers.get(feedId);
    if (!parsed) return false;
    const feed = this.deps.getFeed(feedId);
    const postCount = this.markerPostCount(feed);
    return this.isPostReadLocal(parsed, postIndex, postCount);
  }

  getReadForFeed(feedId: string): Set<number> {
    const result = new Set<number>();
    const parsed = this.parsedMarkers.get(feedId);
    if (!parsed || parsed.newestRead === undefined) return result;

    const feed = this.deps.getFeed(feedId);
    const postCount = this.markerPostCount(feed);
    const minTrackable = this.getMinTrackable(postCount);

    // Optimize: only iterate recent 1024 posts as current implementation does
    const start = Math.max(minTrackable, postCount - 1024);

    for (let postIdx = start; postIdx <= parsed.newestRead && postIdx < postCount; postIdx++) {
      const bitPos = postIdx - minTrackable;
      const byteIdx = Math.floor(bitPos / 8);
      const bitIdx = bitPos % 8;

      if (byteIdx < parsed.bitmap.length && (parsed.bitmap[byteIdx] & (1 << bitIdx))) {
        result.add(postIdx);
      }
    }

    return result;
  }

  /** Ascending indices of all unread posts within the trackable window. Posts below the
   *  window are assumed unread but not returned (their read state can no longer be tracked).
   *  Without any marker state the whole trackable window counts as unread. */
  getUnreadIndices(feedId: string): number[] {
    const postCount = this.markerPostCount(this.deps.getFeed(feedId));
    const minTrackable = this.getMinTrackable(postCount);
    const parsed = this.parsedMarkers.get(feedId);
    const result: number[] = [];
    for (let i = minTrackable; i < postCount; i++)
      if (!parsed || !this.isPostReadLocalDirect(parsed.bitmap, parsed.newestRead, minTrackable, i))
        result.push(i);
    return result;
  }

  getUnreadCount(feedId: string): number {
    // A paused feed never counts as unread, also not in the moment between new posts arriving
    // and the engine marking them read.
    if (this.isPaused(feedId)) return 0;
    // Clamp at 0: the count is maintained incrementally and — in anonymous mode, which has no
    // authoritative server marker state to reconcile against — small drift can otherwise surface
    // as a negative badge. Also heals any value already persisted negative from before this guard.
    return Math.max(0, this.parsedMarkers.get(feedId)?.unreadCount ?? 0);
  }

  // --- Mutators ---

  markPostRead(feedId: string, postIndex: number): void {
    this.queueMarkerChange(feedId, { markAsRead: [postIndex] });
    this.updateLocalMarker(feedId, postIndex, true);
    this.broadcast([feedId]);
  }

  markPostUnread(feedId: string, postIndex: number): void {
    this.queueMarkerChange(feedId, { markAsUnread: [postIndex] });
    this.updateLocalMarker(feedId, postIndex, false);
    this.broadcast([feedId]);
  }

  markPostsRead(feedId: string, postIndices: PostMarker[]): void {
    this.queueMarkerChange(feedId, { markAsRead: postIndices });
    for (const marker of postIndices) {
      if (typeof marker == 'number') {
        this.updateLocalMarker(feedId, marker, true);
      } else {
        for (let i = marker[0]; i <= marker[1]; i++)
          this.updateLocalMarker(feedId, i, true);
      }
    }
    this.broadcast([feedId]);
  }

  markAllRead(feedId: string): void {
    const feed = this.deps.getFeed(feedId);
    const postCount = this.markerPostCount(feed);
    // Always with our postCount: the server must not mark posts that arrive before it gets this.
    this.queueMarkerChange(feedId, { markAllRead: 'all', postCount });

    const parsed = this.parsedMarkers.get(feedId);
    console.log(`[read-markers] local markAllRead: feed ${feedId}: marking all ${postCount} post(s) `
      + `(0-${Math.max(postCount - 1, 0)}) read locally, was ${parsed?.unreadCount ?? 0} unread (queued for server)`);
    if (parsed && parsed.unreadCount > 0) {
      if (postCount == 0) {
        parsed.newestRead = undefined;
        parsed.bitmap = new Uint8Array(0);
        parsed.unreadCount = 0;
      } else {
        const minTrackable = this.getMinTrackable(postCount);
        parsed.newestRead = postCount - 1;

        const bitmapSize = Math.ceil((parsed.newestRead - minTrackable + 1) / 8);
        parsed.bitmap = new Uint8Array(bitmapSize);
        parsed.bitmap.fill(0xFF);

        // Handle partial last byte
        const totalBits = parsed.newestRead - minTrackable + 1;
        if (totalBits % 8 != 0) {
          const validBits = totalBits % 8;
          parsed.bitmap[parsed.bitmap.length - 1] = (1 << validBits) - 1;
        }

        parsed.unreadCount = 0;
      }
      this.notifyUnreadChange(feedId);
    }
    this.broadcast([feedId]);
  }

  markAllUnread(feedId: string): void {
    const feed = this.deps.getFeed(feedId);
    if (!feed) return;
    const postCount = this.markerPostCount(feed);

    this.queueMarkerChange(feedId, { markAsUnread: [[0, postCount - 1]] });

    const parsed = this.parsedMarkers.get(feedId);
    if (parsed) {
      parsed.newestRead = undefined;
      parsed.bitmap = new Uint8Array(0);
      parsed.unreadCount = postCount;
      this.notifyUnreadChange(feedId);
    }
    this.broadcast([feedId]);
  }

  /**
   * Warms the post cache with the first N posts that would be rendered when
   * the user opens this feed — newest-first, unread-only when unread exists.
   * Fire-and-forget; errors are swallowed.
   */
  preloadFeed(feedId: string, count = 10): void {
    const feed = this.deps.getFeed(feedId);
    if (!feed || !feed.postCount) return;

    const postCount = feed.postCount;
    const maxCount = Math.min(postCount, 1000);
    const startPost = postCount - maxCount;

    const readSet = this.getReadForFeed(feedId);
    const indices: number[] = [];
    for (let i = postCount - 1; i >= startPost && indices.length < count; i--)
      if (!readSet.has(i)) indices.push(i);

    if (!indices.length)
      for (let i = postCount - 1; i >= startPost && indices.length < count; i--)
        indices.push(i);

    for (const i of indices)
      getSinglePost(feed, i).catch(() => undefined);
  }

  // --- Lifecycle / data-load hooks ---

  /**
   * Load markers from a freshly loaded feed-list response (enable / full reload).
   * `serverTime` is the response's watermark: unconfirmed patches with a
   * serverTime <= it are provably reflected and dropped; the rest (plus the
   * not-yet-sent pending patch) are re-layered so local marks survive the load.
   */
  loadFromData(feedMarkers: string[], bitmaps: string[] | Record<string, string>, feeds: RestUserFeed[], serverTime: number): void {
    this.parseServerMarkers(feedMarkers, bitmaps, feeds);
    this.drainConfirmed(serverTime);
    this.replayUnconfirmed();
  }

  /** Pure parse: rebuild parsedMarkers from server marker strings. No drain/replay. */
  private parseServerMarkers(feedMarkers: string[], bitmaps: string[] | Record<string, string>, feeds: RestUserFeed[]): void {
    this.parsedMarkers.clear();

    // Bitmaps may arrive as a sparse feedId-keyed Record; normalize to an array aligned with feedMarkers.
    const bitmapArray = decodeBitmaps(bitmaps, feedMarkers);
    for (let i = 0; i < feedMarkers.length; i++) {
      const markerStr = feedMarkers[i];
      const bitmapStr = bitmapArray[i] || '';

      // Extract feedId to look up postCount
      const feedId = markerStr.split('/')[0];
      const feed = feeds.find(f => f.id == feedId);
      const postCount = this.markerPostCount(feed);

      const parsed = this.parseFeedMarker(markerStr, bitmapStr, postCount);
      if (parsed) {
        this.parsedMarkers.set(parsed.feedId, parsed);
      }
    }

    // Ensure all feeds have a marker (empty if not present)
    for (const feed of feeds) {
      if (!this.parsedMarkers.has(feed.id)) {
        this.parsedMarkers.set(feed.id, {
          feedId: feed.id,
          sortOrder: 'A',
          showRead: 'u',
          compactMode: 'f',
          bitmap: new Uint8Array(0),
          unreadCount: Math.min(MAX_TRACKED_POSTS, this.markerPostCount(feed)),
          newestRead: undefined
        });
      }
    }

    // Calculate unread counts for all markers
    for (const feed of feeds) {
      const marker = this.parsedMarkers.get(feed.id);
      if (marker)
        marker.unreadCount = this.calculateUnreadCount(feed.id, this.markerPostCount(feed));
    }
  }

  /**
   * Server-driven marker replacement (from a poll response).
   * Returns the list of feed IDs whose unread count changed, so the caller
   * can broadcast/notify accordingly.
   *
   * Captures oldUnreadCounts BEFORE re-parsing so the change detection sees
   * the right "before" values — see the comment in feed-engine.applyMarkerUpdate.
   * Confirmed unconfirmed-patches are dropped; the rest are re-layered, so a
   * poll that ran before our recent mark reached the DB doesn't visually revert it.
   */
  applyServerMarkerState(feedMarkers: string[], bitmaps: string[] | Record<string, string>, feeds: RestUserFeed[], serverTime: number): string[] {
    const oldUnreadCounts = new Map<string, number>();
    this.parsedMarkers.forEach((m, id) => oldUnreadCounts.set(id, m.unreadCount));

    this.parseServerMarkers(feedMarkers, bitmaps, feeds);
    this.drainConfirmed(serverTime);
    this.replayUnconfirmed();

    const changedFeedIds: string[] = [];
    this.parsedMarkers.forEach((marker, feedId) => {
      const oldCount = oldUnreadCounts.get(feedId) ?? 0;
      if (oldCount != marker.unreadCount) {
        changedFeedIds.push(feedId);
        this.notifyUnreadChange(feedId);
      }
    });

    if (changedFeedIds.length) {
      console.log(`[read-markers] ← server poll: read markers changed for ${changedFeedIds.length} feed(s): `
        + changedFeedIds.map(id => {
            const m = this.parsedMarkers.get(id);
            return `${id} (unread ${oldUnreadCounts.get(id) ?? 0}→${m?.unreadCount}, newestRead=${m?.newestRead ?? 'none'})`;
          }).join(', '));
      this.broadcast(changedFeedIds);
    } else {
      console.log('[read-markers] ← server poll: received markers, no unread-count changes');
    }

    return changedFeedIds;
  }

  /**
   * Handle a post-count growth notification from the poll loop. Caller passes
   * old + new postCount values. Updates unreadCount
   * and shifts the bitmap when postCount crosses MAX_TRACKED_POSTS.
   * Returns true if anything changed (caller should broadcast/notify).
   */
  handlePollPostCountUpdate(feedId: string, oldPostCount: number, newPostCount: number): boolean {
    const marker = this.parsedMarkers.get(feedId);
    const newPostsCount = newPostCount - oldPostCount;

    if (newPostsCount <= 0 || !marker) return false;

    const oldMinTrackable = this.getMinTrackable(oldPostCount);
    const newMinTrackable = this.getMinTrackable(newPostCount);

    // Recalculate the count from the bitmap instead of blindly adding
    // newPostsCount: a cross-tab broadcast from a tab that already knew (and
    // read) some of these "new" posts may have set their bits/newestRead
    // already — a blind += would drift the count above the bitmap, stranding
    // the post list on "All caught up" with a nonzero badge. Recalc also caps
    // the all-unread case at the trackable window.
    const recalc = () =>
      marker.unreadCount = this.calculateUnreadCountLocal(marker.bitmap, marker.newestRead, newMinTrackable, newPostCount);

    // No reads yet, or postCount <= 1000 (bit positions unchanged): no shifting needed
    if (marker.newestRead === undefined || newPostCount <= MAX_TRACKED_POSTS) {
      recalc();
      this.notifyUnreadChange(feedId);
      return true;
    }

    // postCount > 1000, need to shift bitmap
    const shiftAmount = newMinTrackable - oldMinTrackable;

    if (shiftAmount == 0) {
      recalc();
      this.notifyUnreadChange(feedId);
      return true;
    }

    // Check if newestRead falls below trackable range
    if (marker.newestRead < newMinTrackable) {
      marker.newestRead = undefined;
      marker.bitmap = new Uint8Array(0);
      marker.unreadCount = Math.min(MAX_TRACKED_POSTS, newPostCount);
      this.notifyUnreadChange(feedId);
      return true;
    }

    // Shift bitmap
    const newBitmapSize = Math.ceil((marker.newestRead - newMinTrackable + 1) / 8);
    const newBitmap = new Uint8Array(newBitmapSize);

    for (let postIdx = newMinTrackable; postIdx <= marker.newestRead; postIdx++) {
      const oldBitPos = postIdx - oldMinTrackable;
      const oldByteIdx = Math.floor(oldBitPos / 8);
      const oldBitIdx = oldBitPos % 8;

      if (oldByteIdx < marker.bitmap.length &&
          (marker.bitmap[oldByteIdx] & (1 << oldBitIdx))) {
        const newBitPos = postIdx - newMinTrackable;
        newBitmap[Math.floor(newBitPos / 8)] |= (1 << (newBitPos % 8));
      }
    }

    marker.bitmap = newBitmap;

    // Recalculate unread count for accuracy
    marker.unreadCount = this.calculateUnreadCountLocal(
      marker.bitmap, marker.newestRead, newMinTrackable, newPostCount
    );

    this.notifyUnreadChange(feedId);
    return true;
  }

  /** Install a marker for a newly subscribed feed using server-provided marker/bitmap strings. */
  handleSubscribeResult(feed: RestUserFeed, markerStr: string, bitmapStr: string): void {
    const marker = this.parseFeedMarker(markerStr, bitmapStr, this.markerPostCount(feed));
    if (marker)
      this.parsedMarkers.set(feed.id, marker);
  }

  /** Refresh marker unread count for a feed whose first posts just arrived. */
  handleNewFeedPosts(feedId: string, newPostCount: number): void {
    const marker = this.parsedMarkers.get(feedId);
    if (marker) marker.unreadCount = newPostCount;
    this.notifyUnreadChange(feedId);
  }

  handleUnsubscribe(feedId: string): void {
    this.parsedMarkers.delete(feedId);
    delete this.pendingPatch[feedId];
    delete this.pendingPostCounts[feedId];
    for (const u of this.unconfirmed) {
      delete u.patch[feedId];
      delete u.postCounts[feedId];
    }
    this.unconfirmed = this.unconfirmed.filter(u => Object.keys(u.patch).length);
  }

  /** Apply a cross-tab broadcast of marker state. */
  handleBroadcastMessage(serialized: SerializedFeedMarker[]): void {
    const oldUnreadCounts = new Map<string, number>();
    for (const s of serialized) {
      oldUnreadCounts.set(s.feedId, this.parsedMarkers.get(s.feedId)?.unreadCount ?? 0);
      let bitmap = base64ToUint8Array(s.bitmap);
      let unreadCount = s.unreadCount;
      let newestRead = s.newestRead;
      // The bitmap's bit positions are relative to the SENDER's minTrackable. If
      // our post-count basis differs (this tab hasn't seen the newest posts yet,
      // or is ahead), applying it raw shifts every bit — the classic outcome is
      // "unreadCount says 1, bitmap says all read", which strands the post list
      // on "All caught up". Remap into our frame and recompute the count here;
      // posts outside the sender's tracked window stay unread (untracked).
      const localBasis = this.markerPostCount(this.deps.getFeed(s.feedId));
      if (s.basisPostCount != undefined && s.basisPostCount != localBasis && localBasis) {
        const txMin = this.getMinTrackable(s.basisPostCount);
        const rxMin = this.getMinTrackable(localBasis);
        if (newestRead == undefined || newestRead < rxMin) {
          newestRead = undefined;
          bitmap = new Uint8Array(0);
        } else if (txMin != rxMin) {
          const remapped = new Uint8Array(Math.ceil((newestRead - rxMin + 1) / 8));
          for (let post = Math.max(rxMin, txMin); post <= newestRead; post++) {
            const txPos = post - txMin;
            if (bitmap[Math.floor(txPos / 8)] & (1 << (txPos % 8))) {
              const rxPos = post - rxMin;
              remapped[Math.floor(rxPos / 8)] |= 1 << (rxPos % 8);
            }
          }
          bitmap = remapped;
        }
        unreadCount = this.calculateUnreadCountLocal(bitmap, newestRead, rxMin, localBasis);
        console.log(`[read-markers] ← broadcast: feed ${s.feedId} remapped from sender basis `
          + `${s.basisPostCount} to local ${localBasis} (unread ${s.unreadCount}→${unreadCount})`);
      }
      this.parsedMarkers.set(s.feedId, {
        feedId: s.feedId,
        sortOrder: s.sortOrder,
        showRead: s.showRead,
        compactMode: s.compactMode,
        maxUnread: s.maxUnread,
        maxAgeDays: s.maxAgeDays,
        pausedUntil: s.pausedUntil,
        bitmap,
        unreadCount,
        newestRead
      });
    }

    // Re-layer our own un-acked changes on top of the broadcast state. A
    // broadcast is not an authoritative server read, so it can't confirm (drain)
    // our patches — we only re-apply (idempotent, notifications suppressed).
    this.replayUnconfirmed();

    // Notify net unread-count changes for the overwritten feeds.
    for (const s of serialized) {
      if ((oldUnreadCounts.get(s.feedId) ?? 0) != (this.parsedMarkers.get(s.feedId)?.unreadCount ?? 0))
        this.notifyUnreadChange(s.feedId);
    }
  }

  /** True if there are any unsent marker changes (used by beforeunload). */
  hasPendingChanges(): boolean {
    return Object.keys(this.pendingPatch).length > 0;
  }

  /** All changes the server hasn't accepted yet — in-flight sends, then pending ones — merged into
   *  one patch (empty feed entries dropped). In-flight ones count until the send succeeds, so an app
   *  kill mid-request doesn't lose them; re-sending an applied change is harmless. */
  getPendingMarkers(): PendingMarkers {
    const patch: RestReadMarkerPatch = {};
    const postCounts: Record<string, number> = {};
    const sources = [...this.unconfirmed.filter(u => u.serverTime == IN_FLIGHT), { patch: this.pendingPatch, postCounts: this.pendingPostCounts }];
    for (const source of sources)
      for (const [feedId, changes] of Object.entries(source.patch)) {
        if (!Object.keys(changes).length) continue;
        patch[feedId] = patch[feedId] ? mergeFeedPatches(patch[feedId], changes) : changes;
        postCounts[feedId] ??= source.postCounts[feedId];
      }
    return { patch, postCounts };
  }

  /**
   * Take back unsent changes from an earlier session (e.g. stored while offline). They are older
   * than anything pending now, so current pending changes win on conflict. The changes are
   * re-applied to the local markers and scheduled for sending. Feeds no longer subscribed are dropped.
   */
  restorePendingMarkers(restored: PendingMarkers): void {
    const feedIds = Object.keys(restored.patch).filter(id => this.parsedMarkers.has(id));
    if (!feedIds.length) return;
    const oldUnread = new Map(feedIds.map(id => [id, this.getUnreadCount(id)]));
    const patch: RestReadMarkerPatch = {};
    for (const feedId of feedIds) {
      patch[feedId] = restored.patch[feedId];
      this.pendingPatch[feedId] = mergeFeedPatches(restored.patch[feedId], this.pendingPatch[feedId] ?? {});
      this.pendingPostCounts[feedId] ??= restored.postCounts[feedId];
    }
    const prev = this.suppressNotify;
    this.suppressNotify = true;
    try { this.replayPatch(patch, restored.postCounts); }
    finally { this.suppressNotify = prev; }
    for (const feedId of feedIds)
      if (this.getUnreadCount(feedId) != oldUnread.get(feedId)) this.notifyUnreadChange(feedId);
    this.broadcast(feedIds);
    this.scheduleSync();
  }

  /** Flush pending changes to the server. */
  async flush(): Promise<void> {
    return this.flushSync();
  }

  /** Drop all state (called on disable/logout, after flush). */
  clear(): void {
    this.parsedMarkers.clear();
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }
    this.pendingPatch = {};
    this.pendingPostCounts = {};
    this.pendingEventCount = 0;
    this.unconfirmed = [];
  }

  // --- Internal: bitmap math ---

  // Canonical post count for all bitmap / read-marker math. The server builds the read-marker
  // bitmap and the "Unread Posts" aggregate from the same postCount (see api/lib/unread-feed.ts),
  // and both sides must agree: a different count shifts minTrackable and with it every bit
  // position, which used to make mark-read/unread silently no-op for posts in the aggregated
  // Unread Posts list.
  private markerPostCount(feed: RestUserFeed | undefined): number {
    return feed?.postCount ?? 0;
  }

  private getMinTrackable(postCount: number): number {
    return Math.max(0, postCount - MAX_TRACKED_POSTS);
  }

  private calculateUnreadCount(feedId: string, postCount: number): number {
    const parsed = this.parsedMarkers.get(feedId);
    if (!parsed) return 0;

    const minTrackable = this.getMinTrackable(postCount);
    return this.calculateUnreadCountLocal(parsed.bitmap, parsed.newestRead, minTrackable, postCount);
  }

  private calculateUnreadCountLocal(
    bitmap: Uint8Array,
    newestRead: number | undefined,
    minTrackable: number,
    postCount: number
  ): number {
    let count = 0;
    for (let i = minTrackable; i < postCount; i++) {
      if (!this.isPostReadLocalDirect(bitmap, newestRead, minTrackable, i)) {
        count++;
      }
    }
    return count;
  }

  private isPostReadLocal(marker: LocalFeedMarker, postIndex: number, postCount: number): boolean {
    const minTrackable = this.getMinTrackable(postCount);
    return this.isPostReadLocalDirect(marker.bitmap, marker.newestRead, minTrackable, postIndex);
  }

  private isPostReadLocalDirect(
    bitmap: Uint8Array,
    newestRead: number | undefined,
    minTrackable: number,
    postIndex: number
  ): boolean {
    // No reads at all
    if (newestRead === undefined) return false;

    // Post is newer than newestRead (always unread)
    if (postIndex > newestRead) return false;

    // Post below trackable range (assumed unread)
    if (postIndex < minTrackable) return false;

    // Check bitmap
    const bitPos = postIndex - minTrackable;
    const byteIdx = Math.floor(bitPos / 8);
    const bitIdx = bitPos % 8;

    return byteIdx < bitmap.length && !!(bitmap[byteIdx] & (1 << bitIdx));
  }

  // --- Internal: local state update on user mark-read/unread ---

  private updateLocalMarker(feedId: string, postIndex: number, read: boolean) {
    const parsed = this.parsedMarkers.get(feedId);
    if (!parsed) return;

    const feed = this.deps.getFeed(feedId);
    const postCount = this.markerPostCount(feed);

    const wasRead = this.isPostRead(feedId, postIndex);
    if (wasRead == read) return;

    const minTrackable = this.getMinTrackable(postCount);

    // Ignore posts outside trackable range
    if (postIndex < minTrackable || postIndex >= postCount) return;

    if (read) {
      // Case 1: First read post
      if (parsed.newestRead === undefined) {
        parsed.newestRead = postIndex;
        const bitmapSize = Math.ceil((postIndex - minTrackable + 1) / 8);
        parsed.bitmap = new Uint8Array(bitmapSize);
        const bitPos = postIndex - minTrackable;
        parsed.bitmap[Math.floor(bitPos / 8)] |= (1 << (bitPos % 8));
        parsed.unreadCount = Math.max(0, parsed.unreadCount - 1);
        this.notifyUnreadChange(feedId);
        return;
      }

      // Case 2: Post within or below current newestRead
      if (postIndex <= parsed.newestRead) {
        const bitPos = postIndex - minTrackable;
        const byteIdx = Math.floor(bitPos / 8);
        const bitIdx = bitPos % 8;
        if (byteIdx < parsed.bitmap.length) {
          parsed.bitmap[byteIdx] |= (1 << bitIdx);
          parsed.unreadCount = Math.max(0, parsed.unreadCount - 1);
          this.notifyUnreadChange(feedId);
        }
        return;
      }

      // Case 3: Post is newer than newestRead - expand bitmap
      parsed.newestRead = postIndex;

      const newBitmapSize = Math.ceil((postIndex - minTrackable + 1) / 8);
      const newBitmap = new Uint8Array(newBitmapSize);

      // Copy old bitmap (guard against stale bitmap from mismatched minTrackable)
      if (parsed.bitmap.length <= newBitmapSize)
        newBitmap.set(parsed.bitmap);
      // else: bitmap is stale (built with different minTrackable), skip copy - server sync will restore

      // Mark new post as read
      const bitPos = postIndex - minTrackable;
      newBitmap[Math.floor(bitPos / 8)] |= (1 << (bitPos % 8));

      parsed.bitmap = newBitmap;
      parsed.unreadCount = Math.max(0, parsed.unreadCount - 1);
      this.notifyUnreadChange(feedId);
    } else {
      // Mark as unread: just clear the bit if within bitmap range
      if (parsed.newestRead === undefined) return;

      const bitPos = postIndex - minTrackable;
      const byteIdx = Math.floor(bitPos / 8);
      const bitIdx = bitPos % 8;

      if (byteIdx < parsed.bitmap.length) {
        parsed.bitmap[byteIdx] &= ~(1 << bitIdx);
        parsed.unreadCount++;
        this.notifyUnreadChange(feedId);
      }

      // Note: Don't shrink newestRead (performance trade-off)
    }
  }

  // --- Internal: queue + flush patches to server ---

  private queueMarkerChange(feedId: string, change: FeedReadMarkerPatch) {
    if (!this.pendingPatch[feedId]) {
      this.pendingPatch[feedId] = {};
    }

    // Remember when the user last marked anything read, so Home can tell "just caught up"
    // from "nothing new arrived" (see connector/lastMarkRead).
    if (change.markAsRead?.length || change.markAllRead == 'all') recordMarkReadNow();

    this.pendingPatch[feedId] = mergeFeedPatches(this.pendingPatch[feedId], change);
    // Capture the current post-count basis so a later markAllRead replay caps at
    // the count as-of-now (posts that arrive afterwards must stay unread).
    this.pendingPostCounts[feedId] = this.markerPostCount(this.deps.getFeed(feedId));

    this.pendingEventCount++;
    this.scheduleSync();
    this.deps.onPendingChange?.('queued');
  }

  private scheduleSync() {
    // Immediate sync if we've hit the event limit
    if (this.pendingEventCount >= MAX_PENDING_EVENTS) {
      this.flushSync();
      return;
    }

    // Otherwise debounce
    if (this.syncTimer) clearTimeout(this.syncTimer);
    this.syncTimer = setTimeout(() => this.flushSync(), SYNC_DELAY_MS);
  }

  private async flushSync(): Promise<void> {
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }

    const auth = this.deps.getAuth();
    if (!auth || !Object.keys(this.pendingPatch).length) return;

    // Remove feed entries that have no actual changes
    for (const feedId of Object.keys(this.pendingPatch)) {
      if (!Object.keys(this.pendingPatch[feedId]).length)
        delete this.pendingPatch[feedId];
    }
    if (!Object.keys(this.pendingPatch).length) return;

    const patch = this.pendingPatch;
    const postCounts = this.pendingPostCounts;
    this.pendingPatch = {};
    this.pendingPostCounts = {};
    this.pendingEventCount = 0;

    for (const [feedId, changes] of Object.entries(patch)) {
      const parts: string[] = [];
      if (changes.markAllRead) parts.push(`markAllRead=${changes.markAllRead}`);
      if (changes.markAsRead?.length) parts.push(`read=[${formatPostMarkers(changes.markAsRead)}]`);
      if (changes.markAsUnread?.length) parts.push(`unread=[${formatPostMarkers(changes.markAsUnread)}]`);
      if (changes.order) parts.push(`order=${changes.order}`);
      if (changes.showRead) parts.push(`showRead=${changes.showRead}`);
      if (changes.compactMode) parts.push(`compactMode=${changes.compactMode}`);
      console.log(`[read-markers] → server: feed ${feedId}: ${parts.join('; ') || '(meta only)'}`);
    }

    // Track as in-flight BEFORE awaiting, so a poll/broadcast that overwrites
    // local markers during the request still re-layers this patch (the old code
    // cleared pendingPatch before the await, leaving an unrecoverable window).
    const entry: UnconfirmedPatch = { serverTime: IN_FLIGHT, patch, postCounts };
    this.unconfirmed.push(entry);

    try {
      const result = await patchReadMarkers(auth, patch);
      // Confirmed-reflected once a poll/load with serverTime >= this arrives.
      entry.serverTime = result.serverTime;
      this.deps.onPendingChange?.('sent');
    } catch (e) {
      console.error('Failed to sync read markers:', e);
      // Drop the in-flight entry and merge its changes back into pendingPatch so
      // a later flush retries them (newer pending changes take precedence). A real merge, not a
      // spread: a spread let newer markAsRead/markAsUnread lists replace the failed ones.
      this.unconfirmed = this.unconfirmed.filter(u => u != entry);
      for (const [feedId, changes] of Object.entries(patch)) {
        this.pendingPatch[feedId] = mergeFeedPatches(changes, this.pendingPatch[feedId] ?? {});
        this.pendingPostCounts[feedId] ??= postCounts[feedId];
      }
      this.deps.onPendingChange?.('failed');
    }
  }

  // --- Internal: unconfirmed-patch watermark (drain + replay) ---

  // Drop unconfirmed patches the server has provably absorbed: any poll/load
  // whose serverTime >= a patch's serverTime is guaranteed to already reflect it.
  private drainConfirmed(serverTime: number) {
    this.unconfirmed = this.unconfirmed.filter(u => u.serverTime > serverTime);
  }

  // Re-apply, on top of freshly-parsed server state, every still-unconfirmed
  // patch (oldest first) then the not-yet-sent pending patch. Keeps the user's
  // recent marks visible when a poll/load/broadcast overwrote local markers
  // before those marks were reflected server-side. Idempotent (re-applying a
  // change already present is a no-op). Notifications are suppressed; the caller
  // emits net-change notifications.
  private replayUnconfirmed() {
    if (!this.unconfirmed.length && !Object.keys(this.pendingPatch).length) return;
    const prev = this.suppressNotify;
    this.suppressNotify = true;
    try {
      for (const u of this.unconfirmed)
        this.replayPatch(u.patch, u.postCounts);
      this.replayPatch(this.pendingPatch, this.pendingPostCounts);
    } finally {
      this.suppressNotify = prev;
    }
  }

  private replayPatch(patch: RestReadMarkerPatch, postCounts: Record<string, number>) {
    for (const [feedId, changes] of Object.entries(patch))
      this.replayFeedPatch(feedId, changes, postCounts[feedId]);
  }

  // Apply one feed's patch to the current parsed marker without re-queueing it.
  // Order matches the server's merge precedence: markAllRead, then markAsRead,
  // then markAsUnread (unread wins). View prefs (order/showRead/compactMode) are
  // replayed too, since the server echoes them in the marker string and would
  // otherwise clobber an unconfirmed setSortOrder/setCompactMode.
  private replayFeedPatch(feedId: string, changes: FeedReadMarkerPatch, issuePostCount: number | undefined) {
    const parsed = this.parsedMarkers.get(feedId);
    if (!parsed) return;

    if (changes.order) parsed.sortOrder = changes.order;
    if (changes.compactMode) parsed.compactMode = changes.compactMode;
    if (changes.showRead) parsed.showRead = changes.showRead;
    if (changes.maxUnread != undefined) parsed.maxUnread = changes.maxUnread || undefined;
    if (changes.maxAgeDays != undefined) parsed.maxAgeDays = changes.maxAgeDays || undefined;
    if (changes.pausedUntil != undefined) parsed.pausedUntil = changes.pausedUntil || undefined;

    // Cap markAll* at the post count as-of the change so posts that arrived
    // afterwards keep their own (unread) state.
    const cap = changes.postCount ?? issuePostCount ?? this.markerPostCount(this.deps.getFeed(feedId));
    if (changes.markAllRead == 'all') {
      for (let i = this.getMinTrackable(cap); i < cap; i++)
        this.updateLocalMarker(feedId, i, true);
    } else if (changes.markAllRead == 'none') {
      for (let i = this.getMinTrackable(cap); i < cap; i++)
        this.updateLocalMarker(feedId, i, false);
    }

    this.replayPostMarkers(feedId, changes.markAsRead, true);
    this.replayPostMarkers(feedId, changes.markAsUnread, false);
  }

  private replayPostMarkers(feedId: string, markers: PostMarker[] | undefined, read: boolean) {
    if (!markers) return;
    for (const m of markers) {
      if (typeof m == 'number') this.updateLocalMarker(feedId, m, read);
      else for (let i = m[0]; i <= m[1]; i++) this.updateLocalMarker(feedId, i, read);
    }
  }

  // --- Internal: marker string parsing ---

  private parseFeedMarker(markerStr: string, bitmapStr: string, postCount: number): LocalFeedMarker | null {
    // Format: <feedId>/<sortOrder>/<viewMode>/<mode>/<mode-data>
    // viewMode format: <showRead><compactMode>[m<maxUnread>][a<maxAgeDays>][p[<pausedUntil>]] where:
    //   showRead: 'u' (unread only) or 'a' (all)
    //   compactMode: 'c' (compact), 'f' (full) or 'l' (list) - optional for backward compatibility
    //   m/a suffixes: optional auto-mark-read limits; p suffix: paused (see parsePausedUntil)
    const parts = markerStr.split('/');
    if (parts.length < 4) return null;

    const feedId = parts[0];
    const sortOrder = (parts[1] as 'N' | 'O' | 'A' | '?') || 'A';
    const viewMode = parts[2];

    // Parse viewMode - backward compatible with single letter
    const showRead = (viewMode[0] as 'u' | 'a') || 'a';
    const compactMode = (viewMode.length > 1 && /[cfl]/.test(viewMode[1]) ? viewMode[1] as 'c' | 'f' | 'l' : 'f') || 'f';
    const maxUnread = parseInt(viewMode.match(/m(\d+)/)?.[1] ?? '', 10) || undefined;
    const maxAgeDays = parseInt(viewMode.match(/a(\d+)/)?.[1] ?? '', 10) || undefined;
    const head = { feedId, sortOrder, showRead, compactMode, maxUnread, maxAgeDays, pausedUntil: parsePausedUntil(viewMode) };

    const mode = parts[3] as 'r' | '0' | 't' | 'b';
    const minTrackable = this.getMinTrackable(postCount);

    switch (mode) {
      case '0': {
        // All unread
        return {
          ...head,
          bitmap: new Uint8Array(0),
          unreadCount: Math.min(MAX_TRACKED_POSTS, postCount),
          newestRead: undefined
        };
      }
      case 'r': {
        // Read until readUntilIndex
        const readUntilIndex = parseInt(parts[4], 10);

        if (readUntilIndex < minTrackable) {
          // All tracked posts are unread
          return {
            ...head,
            bitmap: new Uint8Array(0),
            unreadCount: Math.min(MAX_TRACKED_POSTS, postCount),
            newestRead: undefined
          };
        }

        const newestRead = readUntilIndex;
        const bitmapSize = Math.ceil((newestRead - minTrackable + 1) / 8);
        const bitmap = new Uint8Array(bitmapSize);
        bitmap.fill(0xFF);

        // Handle partial last byte
        const totalBits = newestRead - minTrackable + 1;
        if (totalBits % 8 != 0) {
          const validBits = totalBits % 8;
          bitmap[bitmap.length - 1] = (1 << validBits) - 1;
        }

        return {
          ...head, bitmap,
          unreadCount: postCount - readUntilIndex - 1,
          newestRead
        };
      }
      case 't': {
        // Read to readToIndex-1
        const readToIndex = parseInt(parts[4], 10);

        if (readToIndex <= minTrackable) {
          // All tracked posts are unread
          return {
            ...head,
            bitmap: new Uint8Array(0),
            unreadCount: Math.min(MAX_TRACKED_POSTS, postCount),
            newestRead: undefined
          };
        }

        const newestRead = readToIndex - 1;
        const bitmapSize = Math.ceil((newestRead - minTrackable + 1) / 8);
        const bitmap = new Uint8Array(bitmapSize);
        bitmap.fill(0xFF);

        // Handle partial last byte
        const totalBits = newestRead - minTrackable + 1;
        if (totalBits % 8 != 0) {
          const validBits = totalBits % 8;
          bitmap[bitmap.length - 1] = (1 << validBits) - 1;
        }

        return {
          ...head, bitmap,
          unreadCount: postCount - readToIndex,
          newestRead
        };
      }
      case 'b': {
        // Bitfield mode - server uses inverted indexing
        const serverFirst = parseInt(parts[4], 10);  // highest index
        const serverLast = parseInt(parts[5], 10);   // lowest index
        const restRead = parts[6] == '1';
        const serverBitmap = bitmapStr ? base64ToUint8Array(bitmapStr) : new Uint8Array(0);

        // Find newestRead by scanning server bitmap
        let newestRead: number | undefined = undefined;

        // Check bitmap range with inverted indexing
        for (let i = 0; i < serverBitmap.length * 8; i++) {
          const byteIdx = Math.floor(i / 8);
          const bitIdx = i % 8;
          if (serverBitmap[byteIdx] & (1 << bitIdx)) {
            const postIndex = serverFirst - i; // inverted
            if (postIndex >= serverLast && postIndex < postCount) {
              if (newestRead === undefined || postIndex > newestRead) {
                newestRead = postIndex;
              }
            }
          }
        }

        // Check restRead area
        if (restRead && serverLast > 0) {
          const candidate = serverLast - 1;
          if (newestRead === undefined || candidate > newestRead) {
            newestRead = candidate;
          }
        }

        if (newestRead === undefined || newestRead < minTrackable) {
          // No read posts in trackable range
          return {
            ...head,
            bitmap: new Uint8Array(0),
            unreadCount: Math.min(MAX_TRACKED_POSTS, postCount),
            newestRead: undefined
          };
        }

        // Build new bitmap: bit 0=minTrackable, covers [minTrackable, newestRead]
        const newBitmapSize = Math.ceil((newestRead - minTrackable + 1) / 8);
        const newBitmap = new Uint8Array(newBitmapSize);

        for (let postIdx = minTrackable; postIdx <= newestRead; postIdx++) {
          let isRead = false;

          if (restRead && postIdx < serverLast) {
            isRead = true;
          } else if (postIdx >= serverLast && postIdx <= serverFirst) {
            const bitPos = serverFirst - postIdx; // inverted
            const byteIdx = Math.floor(bitPos / 8);
            const bitIdx = bitPos % 8;
            if (byteIdx < serverBitmap.length) {
              isRead = !!(serverBitmap[byteIdx] & (1 << bitIdx));
            }
          }

          if (isRead) {
            const newBitPos = postIdx - minTrackable;
            newBitmap[Math.floor(newBitPos / 8)] |= (1 << (newBitPos % 8));
          }
        }

        const unreadCount = this.calculateUnreadCountLocal(newBitmap, newestRead, minTrackable, postCount);

        return { ...head, bitmap: newBitmap, unreadCount, newestRead };
      }
      default:
        return null;
    }
  }

  /** Build SerializedFeedMarker payloads for the given feed IDs. */
  serializeMarkers(feedIds: string[]): SerializedFeedMarker[] {
    const serialized: SerializedFeedMarker[] = [];
    for (const feedId of feedIds) {
      const marker = this.parsedMarkers.get(feedId);
      if (marker) {
        serialized.push({
          feedId: marker.feedId,
          sortOrder: marker.sortOrder,
          showRead: marker.showRead,
          compactMode: marker.compactMode,
          maxUnread: marker.maxUnread,
          maxAgeDays: marker.maxAgeDays,
          pausedUntil: marker.pausedUntil,
          bitmap: uint8ArrayToBase64(marker.bitmap),
          unreadCount: marker.unreadCount,
          newestRead: marker.newestRead,
          basisPostCount: this.markerPostCount(this.deps.getFeed(feedId))
        });
      }
    }
    return serialized;
  }

  // --- Internal: broadcast helper ---

  private broadcast(feedIds: string[]) {
    const serialized = this.serializeMarkers(feedIds);
    if (!serialized.length) return;
    this.deps.broadcastMarkers(feedIds, serialized);
  }
}

function uint8ArrayToBase64(bytes: Uint8Array): string {
  if (!bytes.length) return '';
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToUint8Array(base64: string): Uint8Array {
  try {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  } catch {
    return new Uint8Array(0);
  }
}
